import React, { type ReactNode, createContext, useContext, useEffect, useState } from 'react';

interface AutofillInfoContextType {
  showAutofillInfo: boolean;
  setShowAutofillInfo: (value: boolean) => void;
  toggleAutofill: () => void;
}

const AutofillInfoContext = createContext<AutofillInfoContextType | undefined>(undefined);

interface AutofillInfoProviderProps {
  children: ReactNode;
}

export const AutofillInfoProvider: React.FC<AutofillInfoProviderProps> = ({ children }) => {
  const [showAutofillInfo, setShowAutofillInfo] = useState<boolean>(false);

  useEffect(() => {
    try {
      const showAutofillFromLocalStorage = localStorage.getItem('showAutofill');
      if (showAutofillFromLocalStorage) {
        setShowAutofillInfo(JSON.parse(showAutofillFromLocalStorage));
      }
    } catch (error) {
      console.error('Error reading autofill preference from localStorage:', error);
      setShowAutofillInfo(false);
    }
  }, []);

  const toggleAutofill = (): void => {
    setShowAutofillInfo((prevShowAutofill) => {
      const newShowAutofill = !prevShowAutofill;
      try {
        localStorage.setItem('showAutofill', JSON.stringify(newShowAutofill));
      } catch (error) {
        console.error('Error saving autofill preference to localStorage:', error);
      }
      return newShowAutofill;
    });
  };

  const value = {
    setShowAutofillInfo,
    showAutofillInfo,
    toggleAutofill,
  };

  return <AutofillInfoContext.Provider value={value}>{children}</AutofillInfoContext.Provider>;
};

export const useAutofillInfo = (): AutofillInfoContextType => {
  const context = useContext(AutofillInfoContext);
  if (context === undefined) {
    throw new Error('useAutofillInfo must be used within an AutofillInfoProvider');
  }
  return context;
};
